<script setup lang="ts">
import RcCustomerDialog from '@/components/core/customers/CustomerDialog.vue'
import { optionalValueFallbackFormatter, phoneFormatter } from '@/helpers/ep/formatters'
import { OrderBy } from '@/types/shared/filters'
import type { ICustomerFilters } from '@/types/rc/filters'
import { getAllCustomers, deleteCustomer } from '@/services/core.service'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Search } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { useDebounceFn } from '@vueuse/core'
import { computed, reactive, ref } from 'vue'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import { ElMessage } from 'element-plus'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { useRouter } from 'vue-router'
import type { ICustomer } from '@/types/core/Customer'
import { NotifySuccess } from '@/helpers/core/notifications'

const router = useRouter()

// Vue Query
const queryClient = useQueryClient()

const createDialogVisible = ref<boolean>(false)

const editCustomerTarget = ref<any>()
const fragmentFilter = ref<string>()

const filters = reactive<ICustomerFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  order_by: 'created_at',
  order_by_order: OrderBy.DESC,
  fragment: undefined,
})

const { isFetching, isPending, isError, isRefetching, data, error, refetch } = useQuery({
  queryKey: ['customers', filters],
  queryFn: () => getAllCustomers(filters),
  placeholderData: (prev: any) => prev,
})

// Compute message to display when table is empty.
const tableEmptyText = computed(() => {
  if (isFetching.value && !filters.fragment) return 'Loading...'
  if (isFetching.value && filters.fragment) return `Searching for ${filters.fragment}`
  if (filters.fragment && !isFetching.value) return `No results for "${filters.fragment}"`
  else return 'No data'
})

/**
 * Opens dialog in edit mode.
 */
const onEditCustomer = (customerId: number, e: MouseEvent) => {
  e.stopPropagation()
  editCustomerTarget.value = customerId
  createDialogVisible.value = true
}

const onDeleteCustomer = (customerId: number, e: MouseEvent) => {
  e.stopPropagation()
  DeleteConfirmationModal(
    'Delete Customer',
    'Are you sure you want to delete this customer? This action cannot be undone',
    customerId,
    handleDelete.mutateAsync
  )
}

const handleDelete = useMutation({
  mutationFn: (id: number) => {
    return deleteCustomer(id)
  },
  onSuccess: () => {
    NotifySuccess('Customer deleted successfully')
    queryClient.invalidateQueries({ queryKey: ['customers'] })
  },
  onError: (e) => {
    ElMessage.error({ message: e.message })
  },
})

const handleFragmentFilterInput = useDebounceFn(
  () => {
    if (fragmentFilter.value === undefined || fragmentFilter.value === '') {
      filters.fragment = undefined
      return
    }

    if (fragmentFilter.value.length < 4) {
      return
    }

    filters.fragment = fragmentFilter.value
  },
  500,
  { maxWait: 3000 }
)
</script>

<template>
  <ViewLayout>
    <template v-if="!isError" #controls>
      <el-button type="primary" :disabled="isFetching" @click="createDialogVisible = true">+ Create Customer</el-button>
    </template>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="() => refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <el-input
        v-model="fragmentFilter"
        style="width: 232px"
        placeholder="Search by anything"
        clearable
        :suffix-icon="Search"
        :disabled="isPending"
        @input="handleFragmentFilterInput"
      />
      <el-table
        v-loading="isFetching"
        :data="data?.data"
        :empty-text="tableEmptyText"
        stripe
        row-class-name="cursor-pointer"
        @row-click="
          (customer: ICustomer) => router.push({ name: 'core-customer-profile', params: { customerId: customer.id } })
        "
      >
        <el-table-column prop="full_name" label="Name" :min-width="248">
          <template #default="scope">
            <div class="flex items-center gap-4">
              <el-avatar class="shrink-0" :size="30">{{ scope.row.full_name[0] }}</el-avatar>
              <div class="shrink-0">{{ scope.row.full_name }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="company_name"
          label="Company"
          :min-width="208"
          :formatter="optionalValueFallbackFormatter"
        />
        <el-table-column prop="alias" label="Alias" :min-width="64" :formatter="optionalValueFallbackFormatter" />
        <el-table-column prop="phone" label="Phone number" :formatter="phoneFormatter" :min-width="224" />
        <el-table-column prop="email" label="Email" :min-width="248" />
        <el-table-column :min-width="128">
          <template #default="scope">
            <div class="flex items-center gap-4">
              <el-button link title="Edit" @click="(e: MouseEvent) => onEditCustomer(scope.row.id, e)">
                <Icon icon="material-symbols:edit-outline-rounded" inline height="20" />
              </el-button>
              <el-button title="Delete" link @click="(e: MouseEvent) => onDeleteCustomer(scope.row.id, e)">
                <Icon icon="material-symbols:delete-outline-rounded" inline height="20" />
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="data && data.total_pages > 0"
        v-model:current-page="filters.page"
        v-model:page-size="filters.per_page"
        class="customers__pagination"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        :disabled="isFetching"
        :layout="DEFAULT_PAGINATION_LAYOUT"
        background
      />
      <rc-customer-dialog
        v-model="createDialogVisible"
        :customer-id="editCustomerTarget"
        @closed="editCustomerTarget = undefined"
      />
    </template>
  </ViewLayout>
</template>

<style scoped lang="scss">
.el-button + .el-button {
  margin-left: 0;
}

.customers {
  &__pagination {
    align-self: flex-end;
  }
}
</style>
